import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore, useTestStore } from '../store';
import { api } from '../utils/api';
import { localApi } from '../utils/local-api';
import { ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react';
import PinyinText from '../components/PinyinText';

const Test: React.FC = () => {
  const { level, part } = useParams<{ level: string; part: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const { currentTest: storeCurrentTest, startTest, answerQuestion, nextQuestion, previousQuestion, completeTest, addTestToHistory } = useTestStore();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [isCheckpointSaving, setIsCheckpointSaving] = useState(false);
  const [checkpointError, setCheckpointError] = useState('');
  const bankVersion = searchParams.get('bankVersion') || '2.0';
  const systemType = searchParams.get('systemType') === 'workbook' ? 'workbook' : 'simulation';
  const workbookPart = searchParams.get('workbookPart') || undefined;
  const examScope = `hsk:${bankVersion}:${systemType}:${level || ''}:${part || ''}:${workbookPart || ''}`;
  const currentTest = storeCurrentTest?.examScope === examScope ? storeCurrentTest : null;

  useEffect(() => {
    if (!level || !part || !token) {
      navigate('/');
      return;
    }

    let cancelled = false;
    const loadQuestions = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await api.startTest(parseInt(level), part, 20, token, undefined, {
          bankVersion,
          systemType,
          workbookPart,
        }); // Default to 20 questions; Workbook returns its complete fixed chapter.
        const questions = response.questions.map(q => ({
          id: q.question_id,
          level: parseInt(level),
          part: q.part || part,
          question: q.question_text,
          options: q.options,
          correctAnswer: 0, // This will be determined by the backend
          media: q.media
        }));
        if (!cancelled) startTest(parseInt(level), part, questions, response.test_session_id, 0, undefined, examScope);
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Failed to load questions');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadQuestions();
    return () => { cancelled = true; };
  }, [level, part, token, startTest, navigate, bankVersion, systemType, workbookPart, examScope]);

  const handleAnswer = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
    answerQuestion(answerIndex);
  };

  const handleNext = async () => {
    if (isCheckpointSaving) return;

    if (currentTest && currentTest.currentQuestionIndex < currentTest.questions.length - 1) {
      const isPracticeSession = currentTest.testId?.startsWith('practice-');
      if ((currentTest.currentQuestionIndex + 1) % 10 === 0 && currentTest.testId && token && !isPracticeSession) {
        setIsCheckpointSaving(true);
        setCheckpointError('');

        try {
          const idx = currentTest.currentQuestionIndex;
          const answersObj: Record<string, any> = {};

          currentTest.answers.forEach((ans, i) => {
              if (ans !== null) answersObj[i] = ans;
          });

          await localApi.saveProgress(currentTest.testId, idx, answersObj, token);
        } catch (err) {
          console.error("Checkpoint save failed:", err);
          setCheckpointError('网络连接异常，进度保存失败，请检查网络后重试。');
        } finally {
          setIsCheckpointSaving(false);
          nextQuestion();
        }
      } else {
        nextQuestion();
      }
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    previousQuestion();
  };

  const handleComplete = async () => {
    if (!currentTest) return;

    const session = completeTest();
    if (session && token) {
      try {
        // Convert session to the format expected by the API
        const answers = session.answers.map((answer, index) => ({
          question_id: session.questions[index].id,
          chosen_option_index: answer,
          chose_buzhidao: answer === 4, // "Don't know" option
          time_spent_seconds: 30 // Default time, could be tracked
        }));
        
        await api.submitTest(session.id, answers, token);
        addTestToHistory(session);
        setShowResults(true);
      } catch (err: any) {
        setError(err.message || 'Failed to submit test');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading questions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  if (!currentTest || !currentTest.questions || currentTest.questions.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <p className="text-gray-600">No test data available</p>
      </div>
    );
  }

  const currentQuestion = currentTest.questions[currentTest.currentQuestionIndex];
  
  // Add safety check for currentQuestion
  if (!currentQuestion) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <p className="text-gray-600">Question not found</p>
      </div>
    );
  }
  
  const progress = ((currentTest.currentQuestionIndex + 1) / currentTest.questions.length) * 100;
  const shouldShowPinyin = currentTest.level === 2;
  const renderText = (text: string, className = '') => {
    if (shouldShowPinyin) {
      return <PinyinText text={text} className={className} />;
    }
    return <span className={className}>{text}</span>;
  };

  if (showResults) {
    const score = currentTest.answers.reduce((acc, answer, index) => {
      if (answer === currentTest.questions[index].correctAnswer) {
        return (acc || 0) + 1;
      }
      return acc || 0;
    }, 0);

    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white shadow rounded-lg p-6 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Test Completed!</h2>
          <p className="text-gray-600 mb-4">
            HSK Level {currentTest.level} - {currentTest.part}
          </p>
          <div className="text-4xl font-bold text-primary-600 mb-4">
            {score}/{currentTest.questions.length}
          </div>
          <p className="text-gray-600 mb-6">
            You scored {Math.round(((score || 0) / currentTest.questions.length) * 100)}%
          </p>
          <div className="space-x-4">
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              Back to Dashboard
            </button>
            <button
              onClick={() => navigate('/history')}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              View History
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-xl font-semibold text-gray-900">
              HSK Level {currentTest.level} - {currentTest.part}
            </h1>
            <span className="text-sm text-gray-500">
              Question {currentTest.currentQuestionIndex + 1} of {currentTest.questions.length}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        <div className="p-6">
          <div className="mb-8">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              {renderText(currentQuestion.question)}
            </h2>
            
            <div className="space-y-3">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswer(index)}
                  className={`w-full text-left p-4 border rounded-lg transition-colors ${
                    selectedAnswer === index
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <span className="font-medium mr-2">{String.fromCharCode(65 + index)}.</span>
                  {renderText(option)}
                </button>
              ))}
              
              <button
                onClick={() => handleAnswer(4)}
                className={`w-full text-left p-4 border rounded-lg transition-colors ${
                  selectedAnswer === 4
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <span className="font-medium mr-2">E.</span>
                {renderText('不知道 (Don\'t know)')}
              </button>
            </div>
          </div>

          {checkpointError && (
              <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg border border-red-200">
                  {renderText(checkpointError)}
              </div>
          )}

          <div className="flex justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentTest.currentQuestionIndex === 0 || isCheckpointSaving}
              className="flex items-center px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous
            </button>
            
            <button
              onClick={handleNext}
              disabled={isCheckpointSaving}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCheckpointSaving ? 'Saving...' : currentTest.currentQuestionIndex === currentTest.questions.length - 1 ? 'Complete Test' : 'Next'}
              <ChevronRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Test;
